import { z } from 'zod';
import { Role, type AuthContext } from '@loci/types';

/**
 * Shared utilities for propagating validated user context between services via
 * trusted headers (`X-User-Id`, `X-User-Role`, `X-Session-Id`).
 *
 * These headers MUST only be trusted when the request also passes service-to-service
 * auth (see {@link validateServiceAuth}). Downstream services must strip these
 * headers from any request that does not carry a valid service key, otherwise a
 * malicious client could forge an identity.
 *
 * @see API-1241 / API-1263
 */

/**
 * Canonical header names used to propagate user context across services.
 * Use these constants instead of magic strings to keep producers and consumers
 * in sync.
 */
export const USER_CONTEXT_HEADERS = {
  USER_ID: 'x-user-id',
  USER_ROLE: 'x-user-role',
  SESSION_ID: 'x-session-id',
  /** WEB-39 — optional advertiser scope propagated with service auth. */
  ADVERTISER_ID: 'x-advertiser-id',
} as const;

export type UserContextHeaderName =
  (typeof USER_CONTEXT_HEADERS)[keyof typeof USER_CONTEXT_HEADERS];

/**
 * The validated shape of user context reconstructed from trusted headers.
 * Narrower than `AuthContext` because `email` and `permissions` are not
 * propagated across the wire (they are re-resolved downstream from the role).
 */
export interface PropagatedUserContext {
  userId: string;
  role: Role;
  sessionId?: string;
  /** WEB-39 — present when the caller acts in an advertiser scope. */
  advertiserId?: string;
}

const UUID_SCHEMA = z.string().uuid();
const ROLE_SCHEMA = z.nativeEnum(Role);
const SESSION_ID_SCHEMA = z.string().min(1);
/** Advertiser ids are Prisma cuid() strings (not UUIDs). */
const ADVERTISER_ID_SCHEMA = z.string().min(1).max(128);

const propagatedUserContextSchema = z.object({
  userId: UUID_SCHEMA,
  role: ROLE_SCHEMA,
  sessionId: SESSION_ID_SCHEMA.optional(),
  advertiserId: ADVERTISER_ID_SCHEMA.optional(),
});

/**
 * The subset of `AuthContext` required to build propagation headers.
 * Accepting a minimal shape (rather than the full `AuthContext`) keeps the
 * utility friendly to tests and to callers that construct contexts manually.
 */
export type BuildableAuthContext = Pick<AuthContext, 'userId' | 'role'> &
  Pick<Partial<AuthContext>, 'sessionId' | 'advertiserId'>;

/**
 * Build the trusted header object for an outbound service-to-service call.
 *
 * Throws if the provided context contains malformed values — callers should
 * never be able to forward an invalid `userId` or `role` to downstream services.
 *
 * @example
 * const headers = {
 *   ...getGeoCoreHeaders(),
 *   ...buildUserContextHeaders(req.user),
 * };
 */
export function buildUserContextHeaders(authContext: BuildableAuthContext): Record<string, string> {
  const userId = UUID_SCHEMA.parse(authContext.userId);
  const role = ROLE_SCHEMA.parse(authContext.role);

  const headers: Record<string, string> = {
    [USER_CONTEXT_HEADERS.USER_ID]: userId,
    [USER_CONTEXT_HEADERS.USER_ROLE]: role,
  };

  if (authContext.sessionId !== undefined) {
    headers[USER_CONTEXT_HEADERS.SESSION_ID] = SESSION_ID_SCHEMA.parse(authContext.sessionId);
  }

  if (authContext.advertiserId !== undefined) {
    headers[USER_CONTEXT_HEADERS.ADVERTISER_ID] = ADVERTISER_ID_SCHEMA.parse(
      authContext.advertiserId,
    );
  }

  return headers;
}

/**
 * Minimal request-headers shape — works with `express.Request.headers`,
 * Node's `IncomingHttpHeaders`, or plain test objects.
 */
export type HeaderBag = Record<string, string | string[] | undefined>;

function getHeader(headers: HeaderBag, name: string): string | undefined {
  // HTTP headers are case-insensitive; Node lowercases them on parse, but we
  // also support pass-through test objects that may use any casing.
  const lower = name.toLowerCase();
  let value = headers[lower] ?? headers[name];
  if (value === undefined) {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === lower) {
        value = headers[key];
        break;
      }
    }
  }
  if (Array.isArray(value)) return value[0];
  return value;
}

/**
 * Parse propagated user-context headers into a validated {@link PropagatedUserContext}.
 *
 * Returns `null` when no user-context headers are present (e.g. anonymous
 * downstream calls). Returns a `ZodError`-shaped failure when headers are
 * present but malformed — callers should treat that as a 400/401 and MUST NOT
 * fall back to an implicit identity.
 */
export function extractUserContextFromHeaders(
  headers: HeaderBag,
): { ok: true; context: PropagatedUserContext } | { ok: false; error: z.ZodError } | null {
  const userId = getHeader(headers, USER_CONTEXT_HEADERS.USER_ID);
  const role = getHeader(headers, USER_CONTEXT_HEADERS.USER_ROLE);
  const sessionId = getHeader(headers, USER_CONTEXT_HEADERS.SESSION_ID);
  const advertiserId = getHeader(headers, USER_CONTEXT_HEADERS.ADVERTISER_ID);

  // No propagation headers at all → caller is anonymous.
  // Use `=== undefined` (not a falsy check) so explicit empty-string headers
  // fall through to Zod validation and surface as `{ ok: false }` instead of
  // being silently treated as "anonymous".
  if (
    userId === undefined &&
    role === undefined &&
    sessionId === undefined &&
    advertiserId === undefined
  ) {
    return null;
  }

  const parsed = propagatedUserContextSchema.safeParse({
    userId,
    role,
    sessionId,
    advertiserId,
  });

  if (!parsed.success) {
    return { ok: false, error: parsed.error };
  }

  return { ok: true, context: parsed.data };
}
